import { Fragment } from 'react';

import { Grid, makeStyles, Typography } from '@material-ui/core';

import {
  Entity,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  ANNOTATION_EDIT_URL,
  isSystemEntity,
  isComponentEntity,
  isApiEntity,
} from '@backstage/catalog-model';
import {
  EntityRefLinks,
  EntityRefLink,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { Link } from '@backstage/core-components';

import { toMedidataComponent } from '@internal/plugin-medidata-common';

import { AboutField } from './AboutField';
import { EntityLabel } from '../EntityLabel';
import { stakeholdersAsUsers, StakeholderAndUser } from '../StakeholdersCard';
import { useEntities } from '../../util';

export interface AboutContentProps {
  entity: Entity;
}

export interface AboutInfoFieldsProps {
  entity: Entity;
  isComponent: boolean;
  isSystem: boolean;
}

const useStyles = makeStyles(theme => ({
  label: {
    color: theme.palette.text.secondary,
    fontWeight: 'bold',
  },
  inactive: {
    color: theme.palette.error.main,
    fontWeight: 'bold',
  },
}));

const renderOwner = (
  owner: StakeholderAndUser,
  inactive: string,
  order: number,
) => {
  if (owner.user !== undefined) {
    return (
      <EntityRefLink entityRef={owner.user} defaultKind="user">
        {order > 0 && ', '}
        {owner.user.spec?.profile?.displayName}
      </EntityRefLink>
    );
  }
  return (
    <Typography variant="body2" className={inactive}>
      {order > 0 && ', '}
      {owner.stakeholder.email}
    </Typography>
  );
};

const renderMissingOwner = (editUrl: string) => (
  <Typography variant="body2">
    Owner missing [<Link to={editUrl}>add</Link>]
  </Typography>
);

const renderOwners = (owners: StakeholderAndUser[], inactive: string) =>
  owners.map((owner, i) => (
    <Fragment key={owner.stakeholder.email}>
      {renderOwner(owner, inactive, i)}
    </Fragment>
  ));

const AboutInfoFields = (props: AboutInfoFieldsProps) => {
  const classes = useStyles();
  const { entity, isSystem, isComponent } = props;
  const component = toMedidataComponent(entity);
  const allEntities = useEntities(['User'], ['spec', 'metadata', 'kind']);
  const technicalOwnerEntities = stakeholdersAsUsers(
    component.mdsol.technicalOwners,
    allEntities,
  );
  const productOwnerEntities = stakeholdersAsUsers(
    component.mdsol.productOwners,
    allEntities,
  );
  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);
  const partOfDomainRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'domain',
  });
  const entityMetadataEditUrl =
    entity.metadata.annotations?.[ANNOTATION_EDIT_URL]!;

  return (
    <Grid item xs={10}>
      {isSystem && (
        <Grid>
          <AboutField label="Product Family" value="No Tags" key="domain">
            <EntityRefLinks
              entityRefs={partOfDomainRelations}
              defaultKind="domain"
            />
          </AboutField>
        </Grid>
      )}
      {!isSystem && (
        <AboutField
          label={entity.kind === 'Domain' ? 'Organization' : 'Teams'}
          value="No Owner"
          key="teams"
        >
          {ownedByRelations.length > 0 ? (
            <EntityRefLinks entityRefs={ownedByRelations} defaultKind="group" />
          ) : (
            renderMissingOwner(entityMetadataEditUrl)
          )}
        </AboutField>
      )}
      {isComponent && (
        <AboutField label="Technical owners" value="No Owner" key="tech">
          {technicalOwnerEntities.length > 0
            ? renderOwners(technicalOwnerEntities, classes.inactive)
            : renderMissingOwner(entityMetadataEditUrl)}
        </AboutField>
      )}
      {isComponent && (
        <AboutField label="Product owners" value="No Owner" key="product">
          {productOwnerEntities.length > 0
            ? renderOwners(productOwnerEntities, classes.inactive)
            : renderMissingOwner(entityMetadataEditUrl)}
        </AboutField>
      )}
    </Grid>
  );
};

export const AboutContent = (props: AboutContentProps) => {
  const { entity } = props;

  const isComponent = isComponentEntity(entity);
  const isAPI = isApiEntity(entity);
  const isSystem = isSystemEntity(entity);

  const partOfSystemRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'system',
  });

  const audienceValue = entity.metadata.annotations?.['mdsol/audience'];
  const hasAudience =
    audienceValue !== undefined && audienceValue !== 'unknown';
  const audience = hasAudience ? (
    <EntityLabel labelType="Audience" value={audienceValue} />
  ) : (
    <></>
  );

  const experience =
    entity.spec?.experience !== undefined ? (
      <EntityLabel
        labelType="Experience"
        value={entity.spec?.experience as string}
      />
    ) : (
      <></>
    );

  return (
    <Grid container>
      <Grid container item xs={6} spacing={1}>
        <Grid item xs={12}>
          <AboutField label="OID" value={entity.metadata.name} key="oid">
            <Typography key="oid" variant="body2">
              {entity.metadata.name}
            </Typography>
          </AboutField>
        </Grid>
        <Grid item xs={12}>
          {partOfSystemRelations.length > 0 && (
            <Grid>
              <AboutField label="Product" value="No Tags" key="system">
                <EntityRefLinks
                  entityRefs={partOfSystemRelations}
                  defaultKind="system"
                />
              </AboutField>
            </Grid>
          )}
        </Grid>
        {isComponent && (
          <Grid item xs={12}>
            <AboutField label="Languages" value="No Tags" key="langs">
              {entity?.metadata?.tags?.map(t => (
                <Typography key={t} variant="body2">
                  {' '}
                  {t}{' '}
                </Typography>
              ))}
            </AboutField>
          </Grid>
        )}
      </Grid>

      <Grid container item xs={6} justifyContent="space-between">
        {(isComponent || isAPI) && (
          <Grid item xs={12}>
            <EntityLabel labelType="Type" value={entity?.spec?.type} />
            <EntityLabel
              labelType="Lifecycle"
              value={entity?.spec?.lifecycle}
            />
            {audience}
            {experience}
          </Grid>
        )}
        <AboutInfoFields
          entity={entity}
          isComponent={isComponent}
          isSystem={isSystem}
        />
      </Grid>
    </Grid>
  );
};
